import { EyeIcon } from '@heroicons/react/24/outline';
import { RiskTag, TagSeverity } from '@/components/chariot-ui';
import React from 'react';

import { Button } from '@/components/ui/button';
import { ClickableCount } from '@/components/ClickableCount';
import { Divider } from '@/components/Divider';
import { Loader } from '@/components/Loader';
import { Container } from '@/components/legacy-ui';
import { cn } from '@/utils/classname';

import { VulnerabilityMonitoringSectionProps } from './types';

/**
 * VulnerabilityMonitoringSection - Complete vulnerability monitoring section component
 *
 * Displays vulnerability metrics in a bordered section with:
 * - Section header with title and optional "View" button
 * - 3x2 grid of severity cards (CRITICAL, HIGH, MEDIUM, LOW, INFO, EXPOSURE)
 * - Each card shows Open/Pending counts
 * - Optional clickable counts that trigger navigation callbacks
 * - Loading states with skeleton UI
 * - Severity color coding via RiskTag
 *
 * @example
 * // Basic usage with navigation
 * <VulnerabilityMonitoringSection
 *   metrics={vulnerabilityMetrics}
 *   onViewAll={() => navigate('/vulnerabilities')}
 *   onMetricClick={(severity, status) => navigate(`/vulnerabilities?severity=${severity}&status=${status}`)}
 * />
 *
 * @example
 * // Read-only mode (no actions)
 * <VulnerabilityMonitoringSection
 *   metrics={vulnerabilityMetrics}
 *   showAction={false}
 * />
 */
export const VulnerabilityMonitoringSection: React.FC<
  VulnerabilityMonitoringSectionProps
> = ({
  metrics,
  isLoading = false,
  onViewAll,
  onMetricClick,
  title = 'Vulnerability Monitoring',
  actionLabel = 'View Vulnerabilities',
  showAction = true,
  className,
}) => {
  const severityOptions: Array<{
    severity: TagSeverity;
    open: number;
    pending: number;
  }> = [
    {
      severity: 'critical',
      open: metrics.critical.open,
      pending: metrics.critical.pending,
    },
    {
      severity: 'high',
      open: metrics.high.open,
      pending: metrics.high.pending,
    },
    {
      severity: 'medium',
      open: metrics.medium.open,
      pending: metrics.medium.pending,
    },
    { severity: 'low', open: metrics.low.open, pending: metrics.low.pending },
    {
      severity: 'info',
      open: metrics.info.open,
      pending: metrics.info.pending,
    },
    {
      severity: 'exposure',
      open: metrics.exposure.open,
      pending: metrics.exposure.pending,
    },
  ];

  return (
    <Container variant="emphasized" className={cn(className)}>
      {/* Section Header */}
      <div className="mb-4 flex items-center">
        <h3 className="text-xl font-bold">{title}</h3>
        {showAction && onViewAll && (
          <Button
            className="ml-auto rounded-full"
            size="sm"
            variant="outline"
            onClick={onViewAll}
          >
            <EyeIcon className="size-5" />
            {actionLabel}
          </Button>
        )}
      </div>

      {/* Vulnerability Grid */}
      <div className="grid grid-cols-3 gap-4">
        {severityOptions.map(option => (
          <VulnerabilityCard
            key={option.severity}
            severity={option.severity}
            open={option.open}
            pending={option.pending}
            isLoading={isLoading}
            onCountClick={
              onMetricClick
                ? (status: 'O' | 'T') => onMetricClick(option.severity, status)
                : undefined
            }
          />
        ))}
      </div>
    </Container>
  );
};

/**
 * Internal component for individual vulnerability severity card
 */
const VulnerabilityCard: React.FC<{
  severity: TagSeverity;
  open: number;
  pending: number;
  isLoading: boolean;
  onCountClick?: (status: 'O' | 'T') => void;
}> = ({ severity, open, pending, isLoading, onCountClick }) => {
  return (
    <Container variant="emphasized" className="relative min-w-32 p-4">
      {/* Severity Badge */}
      <RiskTag
        className="absolute -top-3 left-4 rounded-full px-2 py-1 text-xs font-bold uppercase"
        severity={severity}
      />

      {/* Metrics */}
      <Loader className="min-h-14 min-w-14" isLoading={isLoading}>
        <div className="grid grid-cols-2 gap-2 font-bold">
          <span className="text-chariot-text-secondary text-[10px]">Open</span>
          <span className="text-chariot-text-secondary text-[10px]">
            Pending
          </span>
          <Divider className="col-span-2 h-[1px]" />
          {onCountClick ? (
            <>
              <ClickableCount count={open} onClick={() => onCountClick('O')} />
              <ClickableCount
                count={pending}
                onClick={() => onCountClick('T')}
              />
            </>
          ) : (
            <>
              <span>{open}</span>
              <span>{pending}</span>
            </>
          )}
        </div>
      </Loader>
    </Container>
  );
};
